// WHERE A THING LEADS.
//
// A correspondence on its own is a fact. A chain of them is the thing people
// actually come to a table like this for: Saturn is lead, and lead is nothing
// else's, but Saturn is also gold — and so are Jupiter and the Sun. That is a
// route, and routes are what the map is for.
//
// This walks the graph two and three steps out and keeps the strongest routes
// that arrive somewhere genuinely different. It asserts NOTHING: every step is
// an edge somebody already cited, and a chain is a path through them, not a
// new claim. The interface has to say so, because reading a chain as a single
// correspondence is exactly the error this whole section exists to prevent.
const MAX_DEPTH = 3;

export function trailsFrom(startKey, { adjacency, edgesById, nodesByKey, limit = 6 }) {
  const start = nodesByKey.get(startKey);
  if (!start) return [];

  const found = [];
  const seenEnds = new Set([startKey]);

  const walk = (key, path, visited) => {
    if (path.length >= MAX_DEPTH) return;

    (adjacency[key] || []).forEach((edgeId) => {
      const edge = edgesById.get(edgeId);
      if (!edge) return;
      const next = edge.from === key ? edge.to : edge.from;
      if (visited.has(next)) return;

      const step = { edge, from: key, to: next };
      const onward = [...path, step];

      // A route is worth showing when it has actually gone somewhere: at least
      // two steps, and ending on a different kind of thing than it started on.
      const endNode = nodesByKey.get(next);
      if (onward.length >= 2 && endNode && endNode.group !== start.group && !seenEnds.has(next)) {
        found.push({
          end: endNode,
          steps: onward,
          weight: onward.reduce((total, one) => total + (one.edge.weight || 0), 0) / onward.length
        });
        seenEnds.add(next);
      }

      walk(next, onward, new Set([...visited, next]));
    });
  };

  walk(startKey, [], new Set([startKey]));

  return found.sort((a, b) => b.weight - a.weight).slice(0, limit);
}

// "Saturn — metal — Gold — metal — the Sun", for a screen reader and a title.
export function describeTrail(startName, trail, nodesByKey) {
  const names = [startName];
  trail.steps.forEach((step) => {
    names.push(step.edge.label);
    names.push(nodesByKey.get(step.to)?.name || "");
  });
  return names.filter(Boolean).join(" — ");
}

// The shortest way from one thing to another, in edges.
//
// A chain of picks is only a chain if the gaps between them are filled in:
// somebody who chooses Saturn and then the Sun means "show me how those two
// are joined", and the answer runs through gold. Breadth-first, so the answer
// is the FEWEST steps rather than the best-attested ones — a chain is about
// connection, and the evidence for each link is one click away as always.
export function shortestPath(fromKey, toKey, { adjacency, edgesById }) {
  if (fromKey === toKey) return { steps: [], found: true };

  const cameFrom = new Map([[fromKey, null]]);
  const queue = [fromKey];

  while (queue.length > 0) {
    const key = queue.shift();

    for (const edgeId of adjacency[key] || []) {
      const edge = edgesById.get(edgeId);
      if (!edge) continue;
      const next = edge.from === key ? edge.to : edge.from;
      if (cameFrom.has(next)) continue;

      cameFrom.set(next, { key, edge });
      if (next === toKey) {
        const steps = [];
        let at = next;
        while (cameFrom.get(at)) {
          const { key: previous, edge: used } = cameFrom.get(at);
          steps.unshift({ from: previous, to: at, edge: used });
          at = previous;
        }
        return { steps, found: true };
      }
      queue.push(next);
    }
  }

  return { steps: [], found: false };
}

// Every pick, stitched. Where two picks are not joined at all the gap is
// REPORTED rather than quietly skipped: "nothing in this web joins these two"
// is an answer, and a chain drawn with a hole in it pretending otherwise is not.
export function buildChain(keys, graph) {
  const steps = [];
  const gaps = [];
  const nodes = new Set(keys);
  const edges = new Set();

  for (let index = 0; index + 1 < keys.length; index += 1) {
    const leg = shortestPath(keys[index], keys[index + 1], graph);
    if (!leg.found) {
      gaps.push([keys[index], keys[index + 1]]);
      continue;
    }
    leg.steps.forEach((step) => {
      steps.push(step);
      nodes.add(step.from);
      nodes.add(step.to);
      edges.add(step.edge.id);
    });
  }

  return { edges, gaps, nodes, steps };
}
